import React, { useRef, useState } from 'react';
import { netlabApi } from './api';

const CRS_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'EPSG:32632', label: 'UTM 32N (west Austria)' },
  { value: 'EPSG:32633', label: 'UTM 33N (central / east Austria)' },
  { value: 'EPSG:32634', label: 'UTM 34N' },
  { value: 'EPSG:4326', label: 'WGS84 lon/lat' },
  { value: 'custom', label: 'Other EPSG code…' },
];

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function UploadPanel({ onLoaded, onClose }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [crs, setCrs] = useState('auto');
  const [customCrs, setCustomCrs] = useState('EPSG:');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = (f) => {
    setError('');
    if (!f) return;
    if (!/\.json$/i.test(f.name)) {
      setError('Please choose a pandapower .json file.');
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const data = await netlabApi.upload(file, crs === 'custom' ? customCrs.trim() : crs);
      onLoaded(data);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="nl-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="nl-modal nl-upload" role="dialog" aria-label="Upload a pandapower net">
        <div className="nl-modal-head">
          <h2>Upload network</h2>
          <button className="nl-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>

        <div
          className={`nl-dropzone${dragging ? ' dragging' : ''}${file ? ' has-file' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
        >
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 16V4M7 9l5-5 5 5" />
            <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
          </svg>
          {file ? (
            <>
              <div className="nl-drop-title">{file.name}</div>
              <div className="nl-drop-sub">{fmtSize(file.size)} · click or drop to replace</div>
            </>
          ) : (
            <>
              <div className="nl-drop-title">Drop a pandapower JSON net here</div>
              <div className="nl-drop-sub">or click to browse your computer</div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>

        <label className="nl-field">
          <span>Coordinate system of the bus positions</span>
          <select value={crs} onChange={(e) => setCrs(e.target.value)}>
            {CRS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {crs === 'custom' && (
          <label className="nl-field">
            <span>EPSG code</span>
            <input value={customCrs} onChange={(e) => setCustomCrs(e.target.value.toUpperCase())}
              placeholder="EPSG:31256" />
          </label>
        )}
        <p className="nl-hint">
          NetGen nets are stored in the area's UTM zone. Auto-detect recognises lon/lat nets and
          mixed NetGen nets; otherwise it assumes UTM 33N and you can correct it after loading.
        </p>

        {error && <div className="nl-error">{error}</div>}

        <div className="nl-modal-actions">
          <button className="nl-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="nl-btn primary" onClick={submit} disabled={!file || busy}>
            {busy ? 'Loading…' : 'Open in NetLab'}
          </button>
        </div>
      </div>
    </div>
  );
}
